// Outlook / MSO "ghost table" bulletproof-button fallback.
//
// Outlook on Windows renders HTML with the Word engine, which ignores `padding`,
// `border-radius` and inline-block sizing on `<a>` elements — so a styled CTA button
// collapses to a bare text link. To fix this we wrap the exported anchor in a VML
// `<v:roundrect>` that Outlook renders as a real, sized, rounded button, while every
// other email client ignores it via conditional comments (and instead sees the normal
// `<a>`, revealed by the `<!--[if !mso]><!-->` downlevel-revealed comment).
//
// This is applied ONLY at HTML export time (the button component's `toHTML`). The editor
// canvas and the saved `components` JSON — which is what a template reloads from — are left
// untouched, so the edit round-trip stays clean. It is intentionally defensive: if it can't
// derive a safe fallback (no href / no background / no text) it returns the anchor unchanged
// and never emits broken VML.

const VML_NS =
  'xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word"';

const escapeAttr = (s = '') =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// CSS shorthand "20px 28px" -> {top, right, bottom, left}; supports 1/2/3/4 values.
export const parsePadding = (padding = '') => {
  const p = String(padding)
    .trim()
    .split(/\s+/)
    .map((v) => parseFloat(v) || 0);
  const [top = 0, right = top, bottom = top, left = right] = p;
  return { top, right, bottom, left };
};

const px = (value, fallback = 0) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

// rgb()/rgba() -> #rrggbb ; passes existing #hex / named colors through unchanged.
// VML fillcolor/strokecolor do not understand rgb(), so this normalisation matters.
export const toHex = (color = '') => {
  const c = String(color).trim();
  if (!c) return '';
  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (!m) return c;
  const [r, g, b] = m[1]
    .split(',')
    .map((n) => Math.max(0, Math.min(255, parseInt(n, 10) || 0)));
  return (
    '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')
  );
};

// Visible button label (Outlook shows text only; any icon <img> is dropped in the fallback).
// The text is extracted from already-serialized HTML, so its entities (&amp; etc.) are
// already encoded — it is used verbatim in the VML <center> and must NOT be re-escaped.
const plainText = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Wrap an exported button anchor with an Outlook/MSO VML ghost button.
 * @param {Object} style      resolved style of the button component (model.getStyle())
 * @param {string} href       the button's target URL
 * @param {string} anchorHtml the already-serialized `<a>…</a>`
 * @returns {string} MSO-wrapped markup, or `anchorHtml` unchanged if no safe fallback.
 */
export const wrapMsoButton = (style = {}, href = '', anchorHtml = '') => {
  try {
    const text = plainText(anchorHtml);
    const fill = toHex(style['background-color']);
    // Essentials required for a correct ghost button; otherwise ship the plain anchor.
    if (!href || !fill || !text) return anchorHtml;

    const pad = parsePadding(style.padding);
    const fontSize = px(style['font-size'], 16);
    const lineHeight = Math.round(fontSize * 1.2);
    const height = Math.round(pad.top + pad.bottom + lineHeight);
    // Approximate text width (avg glyph ≈ 0.6em) + horizontal padding; err wide to avoid clipping.
    const width = Math.round(pad.left + pad.right + text.length * fontSize * 0.6);
    const radius = px(style['border-radius'], 0);
    // VML arcsize is a percentage of the shorter side; approximate from radius/height.
    const arcsize = height > 0 ? Math.min(50, Math.round((radius / height) * 100)) : 0;
    const color = toHex(style.color) || '#ffffff';
    const fontFamily = style['font-family'] || 'Arial, sans-serif';
    const fontWeight = style['font-weight'] || 'normal';

    return (
      `<!--[if mso]>\n` +
      `<v:roundrect ${VML_NS} href="${escapeAttr(href)}" ` +
      `style="height:${height}px;v-text-anchor:middle;width:${width}px;" ` +
      `arcsize="${arcsize}%" strokecolor="${escapeAttr(fill)}" fillcolor="${escapeAttr(fill)}">\n` +
      `<w:anchorlock/>\n` +
      `<center style="color:${escapeAttr(color)};font-family:${escapeAttr(fontFamily)};` +
      `font-size:${fontSize}px;font-weight:${escapeAttr(fontWeight)};">${text}</center>\n` +
      `</v:roundrect>\n` +
      `<![endif]-->\n` +
      `<!--[if !mso]><!-->${anchorHtml}<!--<![endif]-->`
    );
  } catch (e) {
    // Export must never break because of the Outlook fallback.
    return anchorHtml;
  }
};
